import {
  WSActions,
  WSElement,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { queriesPayerRows, queriesRequirements } from "@query";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";

import { useSession } from "@hooks/useSession";
import { WSQueries } from "../../../../query/WSQuery";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorIsAllRequirementsComplete } from "../../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { getIsGeneralRequirement } from "../../../Requirements/utils";
import { CardAdditionalRequirementsRequester } from "../../components/CardAdditionalRequirementsRequester";

import { PayerRequirements } from "./PayerRequirements";

// The requirements are grouped by `payerAccountId`, which is not a v3
// `Payer.id` — so the name comes off the payer search row keyed by
// `payerAccountId`, the same lookup as
// `useRequirementsConfig.getRequirementPayerName`.
const PayerTitle: React.FC<{ payerAccountId: string }> = ({
  payerAccountId
}) => {
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    { filter: { payerAccountId: { eq: payerAccountId } }, page: { size: 1 } },
    { retry: false }
  );
  const payerRow = queryPayerRow.data?.data?.[0];

  if (!payerRow) {
    return (
      <WSElement shimmer>
        <WSSectionToolbar title="Requirements" mb="M" />
      </WSElement>
    );
  } else {
    return (
      <WSSectionToolbar
        title={`${selectorSearchRowName(payerRow)} requirements`}
        mb="M"
      />
    );
  }
};

export const EligibilityRequirements: React.FC<FlowStep> = ({
  title,
  onNext,
  onSkip
}) => {
  const { accountId } = useSession();

  // TODO(v3-blocker): v1 filtered on `payerPayeeEngagementIds != null` (only
  // engagement-attached requirements) and sorted `updatedAt desc`; v3
  // `listPayeeRequirements` has neither an existence operator nor a sort
  // param, so both are dropped.
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      {
        filter: {
          payeeAccountId: { in: [accountId] }
        }
      },
      { enabled: !!accountId }
    );

  const requirements = queryRequirements.flatData;

  const isCompleted = requirements
    ? selectorIsAllRequirementsComplete(requirements)
    : false;

  const isSkipActive = queryRequirements.isFetched;

  return (
    <WSList gap="2XL">
      <CardAdditionalRequirementsRequester />

      <WSQueries queries={{ queryRequirements }}>
        {() => {
          const nonGeneralRequirements = (requirements ?? []).filter(
            r => !getIsGeneralRequirement(r)
          );

          const requirementsGroupedByPayer = nonGeneralRequirements.reduce(
            (acc, requirement) => {
              const payerAccountId = requirement.payerAccountId;
              if (!payerAccountId) {
                return acc;
              }
              if (!acc[payerAccountId]) {
                acc[payerAccountId] = [];
              }
              acc[payerAccountId].push(requirement);
              return acc;
            },
            {} as Record<string, PayeeRequirement[]>
          );

          return isCompleted ? (
            <WSPanel p="XL">
              <WSSectionToolbar
                title={`All eligibility requirements is completed!`}
                mb="M"
              />
              <WSText.ParagraphSm mt="L" color="gray500">
                Press Continue to proceed to the next step.
              </WSText.ParagraphSm>
            </WSPanel>
          ) : (
            Object.entries(requirementsGroupedByPayer)
              .filter(([payerAccountId]) => payerAccountId !== accountId)
              .map(([payerAccountId, payerRequirements]) => {
                return (
                  <WSElement key={payerAccountId}>
                    <PayerTitle payerAccountId={payerAccountId} />
                    <PayerRequirements requirements={payerRequirements} />
                  </WSElement>
                );
              })
          );
        }}
      </WSQueries>

      <WSActions
        alignment="fill"
        orientation="vertical"
        buttons={[
          {
            label: "Continue",
            kind: "Primary",
            disabled: !isCompleted,
            onClick: onNext
          },
          ...(isSkipActive && !isCompleted
            ? [
                {
                  label: "Skip for now",
                  kind: "Link",
                  onClick: onSkip
                } as const
              ]
            : [])
        ]}
      />
    </WSList>
  );
};
